<template>
    <v-stage
            ref="stage"
            :config="stageConfig"
            @click="handleClick"
            @mousemove="mouseMove"
            @wheel="zoomWheel"
    >
        <v-layer>
            <BackgroundGrid :cellSize="GRID_CELL_SIZE"></BackgroundGrid>
        </v-layer>

        <v-layer>
            <TrimPaint :points="points"></TrimPaint>
        </v-layer>

        <v-layer>
            <Trim></Trim>
        </v-layer>

        <v-layer @click="preventCurrentDrawing">
            <v-rect :config="infoBoxBackground"></v-rect>

            <InfoPlaceholder v-if="isFontsLoaded"
                             :totalBoxWidth="infoBoxWidth"
                             :trimName="trimName"
                             :paintName="currentPaint.name"
                             :textColor="'#333'"
                             :lineColor="'#ddd'"
                             :padding="10"
                             :fontSize="18"
                             @textBoxHeight="setInfoTextBoxHeight"
            ></InfoPlaceholder>

            <BoxListingCanvas
                    :yPos="infoBoxHeight"
                    :rowHeight="38"
                    :rowWidth="infoBoxWidth"
                    :textColor="'#333'"
                    :lineColor="'#ddd'"
            ></BoxListingCanvas>
        </v-layer>

        <v-layer>
            <DrawingLine v-if="drawingState && !trimEditDisabled"
                         :fromPoint="drawLineFromPoint"
                         :toPoint="drawLineToPoint"
                         :lineColor="'#666'"
            ></DrawingLine>
        </v-layer>
    </v-stage>
</template>

<script>
    import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
    import BackgroundGrid from './BackgroundGrid';
    import Trim from './trim/Trim';
    import DrawingLine from './trim/DrawingLine';
    import InfoPlaceholder from "./InfoPlaceholder";
    import BoxListingCanvas from "../boxListing/BoxListingCanvas";
    import TrimPaint from "./trim/paint/TrimPaint";

    export default {
        name: "Stage",

        components: {
            TrimPaint,
            BoxListingCanvas,
            BackgroundGrid,
            Trim,
            DrawingLine,
            InfoPlaceholder,
        },

        data() {
            return {
                isWheeling: false,
                infoBoxHeight: 100,
            }
        },

        computed: {
            ...mapState('trimInfo', [
                'trimName',
            ]),

            ...mapState('editor', [
                'GRID_CELL_SIZE',
                'stageWidth',
                'stageHeight',
                'infoBoxWidth',
                'USER_ACTIONS',
                'userAction',
                'points',
                'drawingState',
                'zoom',
                'zoomStep',
                'isFontsLoaded',
            ]),

            ...mapState('line', [
                'drawLineFromPoint',
                'drawLineToPoint',
            ]),

            ...mapState('paint', [
                'currentPaint',
            ]),

            ...mapGetters('editor', [
                'snapPositionToGrid',
                'trimIsCompleted',
                'trimEditDisabled',
            ]),

            stageConfig() {
                return {
                    width: this.stageWidth,
                    height: this.stageHeight,
                    offsetX: 0.5,
                    offsetY: 0.5,
                    scaleX: this.zoom,
                    scaleY: this.zoom,
                    draggable: !!(this.zoom - 1),
                    dragBoundFunc: this.dragStageBoundFunc,
                }
            },

            infoBoxBackground() {
                return {
                    width: this.infoBoxWidth,
                    height: this.stageHeight,
                    fill: '#f9f9f9',
                }
            },

        },

        methods: {
            ...mapMutations('editor', [
                'setCanvasStage',
                'setUserAction',
                'setDrawingState',
            ]),

            ...mapMutations('line', [
                'setDrawLineToPoint',
            ]),

            ...mapActions('editor', [
                'clickedOnCanvas',
                'stopDrawing',
                'setZoom',
            ]),

            stage() {
                return this.$refs.stage?.getNode();
            },

            preventCurrentDrawing() {
                // that is out of the trim drawing area
                // stop the drawing

                this.stopDrawing();
            },

            // handleClick is the event when the user clicked on the canvas
            handleClick(e) {

                // only left button click
                if (e.evt.button !== 0) return true;

                // get the element (shape) where user had clicked (Stage, Line, Pointer, Hem)
                const shape = e.target;

                // we need to get the position where the user had clicked
                const pointerPosition = this.stage().getRelativePointerPosition();

                this.clickedOnCanvas({ pointerPosition, shape });
            },

            mouseMove() {
                if (this.userAction === this.USER_ACTIONS.DRAW_LINE && this.drawingState) {

                    // while drawing a line we need to get the current pointer
                    // which is the end edge of the drawing line
                    const position = this.stage().getRelativePointerPosition();

                    if (this.isMousePointerOutDrawArea(position)) return;

                    let drawLineToPoint = this.snapPositionToGrid(position);

                    this.setDrawLineToPoint(drawLineToPoint);
                }
            },

            isMousePointerOutDrawArea(position) {
                const boxListingWidth = 250;

                let xOut = position.x <= boxListingWidth;

                return xOut;
            },

            zoomWheel(e) {
                e.evt.preventDefault();

                this.isWheeling = true;

                const stage = this.stage();
                const pointer = stage.getPointerPosition();

                let mousePointTo = {
                    x: (pointer.x - stage.x()) / this.zoom,
                    y: (pointer.y - stage.y()) / this.zoom,
                };

                // how to scale? Zoom in? Or zoom out?
                const direction = e.evt.deltaY > 0 ? 1 : -1;

                const newZoom = direction > 0 ? this.zoom - this.zoomStep : this.zoom + this.zoomStep;

                // new position related to the pointer
                let newPos = {
                    x: pointer.x - mousePointTo.x * newZoom,
                    y: pointer.y - mousePointTo.y * newZoom,
                };

                this.setZoom(newZoom).then(() => {
                    if (newZoom !== this.zoom) return;

                    this.setStagePosition(newPos);
                });

                this.isWheeling = false;
            },

            setStagePosition(pos) {
                // limit new position related to the stage bounds
                let boundPos = this.dragStageBoundFunc(pos);

                this.stage().position(boundPos);
            },

            dragStageBoundFunc({x, y}) {
                // function for set the canvas drag limits

                const xMin = -this.stageWidth * (this.zoom - 1);
                const yMin = -this.stageHeight * (this.zoom - 1);

                let newX = Math.min(0, Math.max(x, xMin));
                let newY = Math.min(0, Math.max(y, yMin));

                return {x: newX, y: newY};
            },

            centerOnZoom(oldZoom, newZoom) {
                // when changing the zoom using the buttons, the canvas is zoomed relative to its center

                const halfWidth = this.stageWidth / 2;
                const halfHeight = this.stageHeight / 2;

                const x = halfWidth - (halfWidth - this.stage().x()) / oldZoom * newZoom;
                const y = halfHeight - (halfHeight - this.stage().y()) / oldZoom * newZoom;

                return {x, y};
            },

            setInfoTextBoxHeight(value) {
                this.infoBoxHeight = value;
            },

        },

        watch: {
            zoom(newValue, oldValue) {
                if (this.isWheeling) return;

                // zoom relative to the center when using the zoom buttons on the control panel
                const pos = this.centerOnZoom(oldValue, newValue);

                this.setStagePosition(pos);
            },
        },

        mounted() {
            this.setCanvasStage(this.stage());
        },

    }
</script>

<style scoped>

</style>